// src/components/OccasionsHubPage.jsx
// Hub page listing all greeting card occasions
// Dynamically pulls categories from the templates API

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listTemplates } from '../utils/api';
import { getOccasionData } from '../utils/occasions';
import SEO, { createBreadcrumbData } from './SEO';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/lottie/loading.json';

// Group occasions by type for better organization
// These will be matched against actual categories from the API
const OCCASION_GROUPS = [
  {
    title: 'Holiday Cards',
    description: 'Celebrate the seasons with festive greeting cards',
    matchPatterns: ['christmas', 'hanukkah', 'chanukah', 'new year', 'valentine', 'easter', 'thanksgiving', 'halloween'],
  },
  {
    title: 'Family Celebrations',
    description: 'Honor the special people in your life',
    matchPatterns: ['mother', 'father', 'birthday', 'anniversary', 'parent'],
  },
  {
    title: 'Life Milestones',
    description: 'Mark important moments and achievements',
    matchPatterns: ['graduation', 'wedding', 'baby', 'engagement', 'retirement', 'promotion'],
  },
  {
    title: 'Thoughtful Messages',
    description: 'Express your feelings with heartfelt cards',
    matchPatterns: ['thank', 'get well', 'sympathy', 'sorry', 'thinking', 'miss you', 'congratulation'],
  },
];

function LoadingSpinner() {
  return (
    <div className="w-12 h-12">
      <Lottie animationData={loadingAnimation} loop={true} autoplay={true} />
    </div>
  );
}

export default function OccasionsHubPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedOccasions, setGroupedOccasions] = useState([]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch categories from API
  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      try {
        const { data } = await listTemplates();
        const cats = Object.keys(data);
        setCategories(cats);
        
        // Group occasions based on patterns
        const grouped = OCCASION_GROUPS.map(group => {
          const matchedCategories = cats.filter(cat => {
            const lowerCat = cat.toLowerCase();
            return group.matchPatterns.some(pattern => 
              lowerCat.includes(pattern) || pattern.includes(lowerCat)
            );
          });
          
          return {
            ...group,
            occasions: matchedCategories.map((cat, index) => getOccasionData(cat, index)),
          };
        }).filter(group => group.occasions.length > 0);
        
        setGroupedOccasions(grouped);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchCategories();
  }, []);

  // Build all occasions for the "All Occasions" grid
  const allOccasions = categories.map((cat, index) => getOccasionData(cat, index));

  // Find popular/seasonal occasions (prioritize based on current season)
  const getPopularOccasions = () => {
    const month = new Date().getMonth();
    const seasonal = [];
    
    // Winter holidays (Nov-Jan)
    if (month >= 10 || month <= 1) {
      seasonal.push('christmas', 'hanukkah', 'new year');
    }
    // Valentine's (Jan-Feb)
    if (month >= 0 && month <= 1) {
      seasonal.push('valentine');
    }
    // Spring (Mar-May)
    if (month >= 2 && month <= 4) {
      seasonal.push('easter', 'mother');
    }
    // Summer (Jun-Aug)
    if (month >= 5 && month <= 7) {
      seasonal.push('father', 'graduation');
    }
    
    // Always include birthday
    seasonal.push('birthday');
    
    // Find matching categories
    return categories
      .filter(cat => {
        const lower = cat.toLowerCase();
        return seasonal.some(s => lower.includes(s));
      })
      .slice(0, 4)
      .map((cat, index) => getOccasionData(cat, index));
  };

  const popularOccasions = getPopularOccasions();

  const breadcrumbData = createBreadcrumbData([
    { name: 'Home', path: '/' },
    { name: 'Occasions', path: '/occasions' },
  ]);

  const collectionsData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Greeting Card Occasions',
    description: 'Browse all greeting card occasions and find the perfect template for any celebration',
    url: 'https://dreamableai.com/occasions',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: allOccasions.map((occasion, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `${occasion.name} Cards`,
        url: `https://dreamableai.com/occasions/${occasion.slug}`,
      })),
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
        <span className="ml-4 text-gray-600">Loading occasions...</span>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Greeting Card Occasions"
        description="Browse all greeting card occasions at DreamableAI. Find templates for Christmas, birthdays, weddings, thank you cards, and every special moment. Create personalized cards with AI."
        path="/occasions"
        structuredData={[breadcrumbData, collectionsData]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            Greeting Cards for Every Occasion
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
            From holidays to milestones, find the perfect card template for any celebration. 
            Customize with AI or start from beautiful professionally designed templates.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Create with AI ✨
            </Link>
            <a
              href="#occasions"
              className="px-8 py-4 bg-white/20 text-white border-2 border-white/50 rounded-full font-bold text-lg hover:bg-white/30 transition-all"
            >
              Browse All Occasions
            </a>
          </div>
        </div>
      </section>

      {/* Quick Links - Popular Occasions */}
      {popularOccasions.length > 0 && (
        <section className="py-12 bg-white border-b">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-lg font-semibold text-gray-500 text-center mb-6">
              Popular Right Now
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {popularOccasions.map(occ => (
                <Link
                  key={occ.slug}
                  to={`/occasions/${occ.slug}`}
                  className="px-5 py-2.5 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100 transition font-medium text-lg"
                >
                  {occ.emoji} {occ.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Occasions by Category */}
      {groupedOccasions.length > 0 && (
        <section id="occasions" className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {groupedOccasions.map((group, groupIndex) => (
              <div key={groupIndex} className="mb-16 last:mb-0">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    {group.title}
                  </h2>
                  <p className="text-gray-600 text-lg">{group.description}</p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {group.occasions.map(occasion => (
                    <Link
                      key={occasion.slug}
                      to={`/occasions/${occasion.slug}`}
                      className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                    >
                      <div className={`bg-gradient-to-br ${occasion.gradient} p-6 text-center`}>
                        <span className="text-5xl block mb-2 group-hover:scale-110 transition-transform duration-300">
                          {occasion.emoji}
                        </span>
                        <h3 className="text-xl font-bold text-white">
                          {occasion.name} Cards
                        </h3>
                      </div>
                      <div className="p-5">
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                          {occasion.heroSubtitle}
                        </p>
                        <span className="text-indigo-600 font-medium text-sm group-hover:underline">
                          Browse templates →
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* All Occasions Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8">
            All Greeting Card Occasions ({allOccasions.length})
          </h2>
          {allOccasions.length === 0 ? (
            <p className="text-center text-gray-600">No template categories available yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {allOccasions.map(occasion => (
                <Link
                  key={occasion.slug}
                  to={`/occasions/${occasion.slug}`}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 transition font-medium"
                >
                  <span className="text-xl">{occasion.emoji}</span>
                  <span className="truncate">{occasion.name}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">
            How DreamableAI Works
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎨</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Choose Your Style</h3>
              <p className="text-gray-600">
                Pick from hundreds of professional templates or describe your vision and let AI create something unique.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✏️</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Personalize</h3>
              <p className="text-gray-600">
                Add your photos, write heartfelt messages, choose fonts and colors that match your style.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📤</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Share Instantly</h3>
              <p className="text-gray-600">
                Send your card via email, shareable link, or download to print. Delivery is instant and free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Create Something Special?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start your 3-day free trial and unlock unlimited card creation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Start Creating Now
            </Link>
            <Link
              to="/subscribe"
              className="px-8 py-4 bg-white/20 text-white border-2 border-white/50 rounded-full font-bold text-lg hover:bg-white/30 transition-all"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}


